import { authService } from '../../../service/auth-service'
import { logger } from '../../../util/logger'
import { ErrorMessageHint } from '../../util/error-message-hint'
import { InputCode } from '../../util/input-code'
import { PasswordToggle } from '../../util/password-toggle'
import { OverlaySpinner } from '../../util/spinner/overlay-spinner'
import style from './forgot-password.module.scss'
import { browserUtil, passwordService } from '@grndhouse/react-common'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useState } from 'react'
import { Button, Form } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

const verifyForgotPasswordEmailSchema = yup
  .object()
  .shape({
    emailVerificationCode: yup
      .string()
      .test('len', 'Email verification code must be exactly 6 characters', (val) => (val ?? '').length === 6)
      .required('Email verification code is required.'),
  })
  .required()

const setNewPasswordSchema = yup
  .object()
  .shape({
    password: passwordService.passwordValidationObject,
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), null], 'Your passwords do not match. Please try again.')
      .required('Confirm password is required.'),
  })
  .required()

type FormValues = {
  password: string
  confirmPassword: string
}

export const VerifyForgotPasswordEmailStep = (props: { email?: string }): React.ReactElement => {
  const { email } = props
  const [errorMessage, setErrorMessage] = useState<string>()
  const [emailVerificationCode, setEmailVerificationCode] = useState('')
  const [codeInvalid, setCodeInvalid] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [passwordVisible, setPasswordVisible] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: yupResolver(setNewPasswordSchema) })

  const onSubmit = async (data: FormValues): Promise<void> => {
    try {
      if (setIsLoading) setIsLoading(true)
      setErrorMessage(undefined)
      const isValid = await verifyForgotPasswordEmailSchema.isValid({ emailVerificationCode })
      setCodeInvalid(!isValid)
      if (!isValid || !email) return // TODO throw some error if email is null or handle on component init
      const { password } = data
      await authService.resetPassword({ email, code: emailVerificationCode, password })
      browserUtil.redirect('/')
    } catch (err) {
      logger.error(err)
      setErrorMessage(err.message)
    } finally {
      if (setIsLoading) setIsLoading(false)
    }
  }

  const handleResendEmail = async (): Promise<void> => {
    try {
      if (setIsLoading) setIsLoading(true)
      if (!email) return // TODO throw some error if email is null or handle on component init
      await authService.requestResetPassword({ email })
    } catch (err) {
      logger.error(err)
    } finally {
      if (setIsLoading) setIsLoading(false)
    }
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <h1 className="text-center">Verify email address</h1>
        <Form.Group className={style.inputContainerGroup}>
          <InputCode onValueChanged={setEmailVerificationCode} isInvalid={codeInvalid} />
          <p className="text-center mt-5">
            Please enter the 6-digit code on the email sent to <strong>{email}</strong>
          </p>
          <Form.Group>
            <PasswordToggle onToggle={setPasswordVisible} isVisible={passwordVisible}>
              <Form.Control
                type={passwordVisible ? 'text' : 'password'}
                placeholder="Password"
                {...register('password')}
                className={`form-control ${errors.password ? 'is-invalid' : ''}`}
              />
              <Form.Control.Feedback type="invalid">{errors.password && <p>{errors.password.message}</p>}</Form.Control.Feedback>
            </PasswordToggle>
          </Form.Group>
          <Form.Group>
            <PasswordToggle onToggle={setPasswordVisible} isVisible={passwordVisible}>
              <Form.Control
                type={passwordVisible ? 'text' : 'password'}
                placeholder="Confirm password"
                {...register('confirmPassword')}
                className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
              />
              <Form.Control.Feedback type="invalid">
                {errors.confirmPassword && <p>{errors.confirmPassword.message}</p>}
              </Form.Control.Feedback>
            </PasswordToggle>
          </Form.Group>
          <div className="text-center">
            <Button className="btn btn-link" onClick={handleResendEmail}>
              Resend Email
            </Button>
          </div>
          <ErrorMessageHint message={errorMessage} />
        </Form.Group>
        <Form.Group className="form-group">
          <Button type="submit" className="w-100">
            Continue
          </Button>
        </Form.Group>
      </Form>
      {isLoading && <OverlaySpinner />}
    </>
  )
}
